import { useEffect, useMemo, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Field, TextInput, NumberInput, SelectInput, TextArea, SuggestInput } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useMasters, opt, vehicleOptions, matchVehicle, partiesByRole, partiesByRoles, useLabourRate } from "@/lib/useMasters";
import { useAuth } from "@/context/AuthContext";

const CHARGE_WHO = [
  { value: "COMPANY", label: "Company bear" },
  { value: "STOCK_OWNER", label: "Stock Owner" },
  { value: "SENDER", label: "Sender" },
];

function resolveChargeParty(who, stockOwnerId, senderId) {
  if (who === "STOCK_OWNER") return stockOwnerId ? +stockOwnerId : null;
  if (who === "SENDER") return senderId ? +senderId : null;
  return null;
}

function dalaRecoverFrom(lab, stockOwnerId, senderId) {
  if (lab?.dala_recovery_from) return lab.dala_recovery_from;
  if (!lab?.dala_recovery_party_id) return "COMPANY";
  if (senderId && String(lab.dala_recovery_party_id) === String(senderId)) return "SENDER";
  if (String(lab.dala_recovery_party_id) === String(stockOwnerId)) return "STOCK_OWNER";
  return "COMPANY";
}

function bagChargeToFrom(detail) {
  const row = (detail.bags_detail || []).find((b) => b.charged_to_id);
  if (!row) return "STOCK_OWNER";
  if (row.charge_to_role) return row.charge_to_role;
  if (String(row.charged_to_id) === String(detail.sender_id)) return "SENDER";
  return "STOCK_OWNER";
}

export function InwardEditDialog({ record, onClose, onSaved }) {
  const m = useMasters();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const loadedId = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const { data: detail, isLoading } = useQuery({
    queryKey: ["txn", "inward", record?.id],
    queryFn: async () => (await api.get(`/txn/inward/${record.id}`)).data,
    enabled: !!record?.id,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!record) {
      loadedId.current = null;
      setF(null);
    }
  }, [record]);

  useEffect(() => {
    if (!detail || !record?.id) return;
    if (loadedId.current === record.id) return;
    loadedId.current = record.id;
    const spill = (detail.bags_detail || []).find((b) => b.charge_type === "Spillage");
    const torn = (detail.bags_detail || []).find((b) => b.charge_type === "Torn");
    const spillQty = spill?.quantity || 0;
    const tornQty = torn?.quantity || 0;
    const lab = detail.labour || {};
    setF({
      date: detail.date ? String(detail.date).slice(0, 10) : "",
      time: detail.time || "",
      warehouse_id: String(detail.warehouse_id || ""),
      stack_id: String(detail.stack_id || ""),
      vehicle_id: detail.vehicle_id ? String(detail.vehicle_id) : "",
      vehicle_number: detail.vehicle_number || "",
      driver: detail.driver || "",
      stock_owner_id: String(detail.stock_owner_id || ""),
      sender_id: detail.sender_id ? String(detail.sender_id) : "",
      source_type: detail.source_type || "",
      source_reference: detail.source_reference || "",
      stock_item_id: String(detail.stock_item_id || ""),
      variety: detail.variety || "",
      grade: detail.grade || "",
      bag_type_id: detail.bag_type_id ? String(detail.bag_type_id) : "",
      bags: String(Math.max((detail.bags || 0) - spillQty, 0)),
      net_weight: detail.net_weight != null ? String(detail.net_weight) : "",
      torn_qty: tornQty ? String(tornQty) : "",
      spill_qty: spillQty ? String(spillQty) : "",
      remarks: detail.remarks || "",
      lab_contractor_id: lab.contractor_id ? String(lab.contractor_id) : "",
      lab_rate: lab.rate != null ? String(lab.rate) : "",
      bag_lab_rate: "",
      expected_dala: lab.expected_dala != null ? String(lab.expected_dala) : "",
      dala_from_vehicle: lab.dala_from_vehicle != null ? String(lab.dala_from_vehicle) : "",
      dala_recover_from: dalaRecoverFrom(lab, detail.stock_owner_id, detail.sender_id),
      bag_charge_to: bagChargeToFrom(detail),
    });
  }, [detail, record?.id]);

  const stockOwnerOpts = opt(partiesByRole(m.parties, "Stock Owner"), (p) => `${p.name} (${p.code})`);
  const senderOpts = opt(partiesByRoles(m.parties, ["Sender", "Buyer", "Seller"]), (p) => `${p.name} (${p.code})`);
  const labourOpts = opt(partiesByRole(m.parties, "Labour Contractor"), (p) => `${p.name} (${p.code})`);
  const stackOpts = opt(
    m.stacks.filter((s) => String(s.warehouse_id) === String(f?.warehouse_id)),
    (s) => s.code,
  );

  const { data: labRate } = useLabourRate(f?.lab_contractor_id, f?.bag_type_id);
  useEffect(() => {
    if (!f?.lab_contractor_id || !f?.bag_type_id || !labRate?.found) return;
    setF((s) => {
      if (!s) return s;
      const next = { ...s };
      if (s.lab_rate !== String(labRate.unloading_rate)) next.lab_rate = String(labRate.unloading_rate);
      if (s.bag_lab_rate !== String(labRate.replace_fill_rate ?? "")) next.bag_lab_rate = String(labRate.replace_fill_rate ?? "");
      return next;
    });
  }, [labRate, f?.lab_contractor_id, f?.bag_type_id]);

  const vehicleBags = Number(f?.bags || 0);
  const tornQty = Number(f?.torn_qty || 0);
  const spillQty = Number(f?.spill_qty || 0);
  const stockBags = vehicleBags + spillQty;
  const bagType = useMemo(
    () => (m.bagTypes || []).find((b) => String(b.id) === String(f?.bag_type_id)),
    [m.bagTypes, f?.bag_type_id],
  );
  const bagRate = Number(bagType?.charge_rate || 0);

  const save = async () => {
    if (!f) return;
    if (!f.warehouse_id || !f.stack_id || !f.stock_owner_id || !f.stock_item_id || !f.bags) {
      return toast.error("Warehouse, stack, stock owner, item and bags are required.");
    }
    const labRateNum = Number(f.lab_rate || 0);
    const bagLabRateNum = Number(f.bag_lab_rate || 0);
    const chargeBags = tornQty + spillQty;
    const dalaShort = Math.max(Number(f.expected_dala || 0) - Number(f.dala_from_vehicle || 0), 0);
    if (dalaShort > 0 && f.dala_recover_from === "SENDER" && !f.sender_id) {
      return toast.error("Select a sender to recover the dala shortfall from them.");
    }
    if (chargeBags > 0 && f.bag_charge_to === "SENDER" && !f.sender_id) {
      return toast.error("Select a sender to charge replacement bags to them.");
    }
    if (chargeBags > 0 && !f.lab_contractor_id) {
      return toast.error("Select a labour contractor — torn/spillage need bag labour (replace/fill) as well as bag material.");
    }
    if (chargeBags > 0 && !bagLabRateNum) {
      return toast.error("No Replace/Fill rate in the contractor agreement for this bag type. Set it under Masters → Labour Rates.");
    }
    setSaving(true);
    try {
      const contractorId = f.lab_contractor_id ? +f.lab_contractor_id : null;
      const chargedTo = resolveChargeParty(f.bag_charge_to, f.stock_owner_id, f.sender_id);
      const body = {
        date: f.date,
        time: f.time,
        warehouse_id: +f.warehouse_id,
        stack_id: +f.stack_id,
        vehicle_id: matchVehicle(m.vehicles, f.vehicle_id, f.vehicle_number)?.id || null,
        driver: f.driver,
        stock_owner_id: +f.stock_owner_id,
        sender_id: f.sender_id ? +f.sender_id : null,
        source_type: f.source_type,
        source_reference: f.source_reference,
        stock_item_id: +f.stock_item_id,
        variety: f.variety,
        grade: f.grade,
        bag_type_id: f.bag_type_id ? +f.bag_type_id : null,
        bags: stockBags,
        net_weight: +f.net_weight || 0,
        remarks: f.remarks,
        labour: f.lab_contractor_id
          ? {
              contractor_id: contractorId,
              quantity: vehicleBags,
              rate: labRateNum,
              work_charged_to_id: +f.stock_owner_id,
              expected_dala: +f.expected_dala || 0,
              dala_from_vehicle: +f.dala_from_vehicle || 0,
              dala_recovery_party_id: resolveChargeParty(f.dala_recover_from, f.stock_owner_id, f.sender_id),
              dala_recovery_from: f.dala_recover_from,
            }
          : null,
        bags_detail: [
          tornQty && {
            charge_type: "Torn", quantity: tornQty, rate: bagRate,
            labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
            charged_to_id: chargedTo, charge_to_role: f.bag_charge_to, reason: "Torn bags replaced",
          },
          spillQty && {
            charge_type: "Spillage", quantity: spillQty, rate: bagRate,
            labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
            charged_to_id: chargedTo, charge_to_role: f.bag_charge_to, reason: "Bags filled from spillage",
          },
        ].filter(Boolean),
      };
      await api.put(`/txn/inward/${record.id}`, body);
      toast.success("Inward updated");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="inward-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Inward {detail?.inward_number || record?.inward_number || record?.doc_number}</DialogTitle>
        </DialogHeader>
        {isLoading || !f ? (
          <div className="grid place-items-center py-12"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
        ) : (
          <>
            <p className="text-xs text-stone-500 -mt-2">Labour, dala and bag charges can always be corrected. Bags and net weight can be corrected even when later movements exist. Warehouse / stack / owner / item can only change if this is still the latest movement on the lot.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Date"><TextInput type="date" value={f.date} onChange={set("date")} /></Field>
              <Field label="Time"><TextInput type="time" value={f.time} onChange={set("time")} /></Field>
              <Field label="Warehouse" required>
                <SelectInput options={opt(m.warehouses, (w) => w.name)} value={f.warehouse_id} onChange={(e) => setF((s) => ({ ...s, warehouse_id: e.target.value, stack_id: "" }))} />
              </Field>
              <Field label="Stack" required>
                <SelectInput options={stackOpts} value={f.stack_id} onChange={set("stack_id")} />
              </Field>
              <Field label="Vehicle">
                <SuggestInput
                  placeholder="Vehicle number"
                  value={f.vehicle_number}
                  options={vehicleOptions(m.vehicles)}
                  onChange={(v) => setF((s) => ({ ...s, vehicle_number: v, vehicle_id: "" }))}
                  onSelect={(o) => setF((s) => ({ ...s, vehicle_id: o.value, vehicle_number: o.label, driver: o.raw?.driver || s.driver }))}
                />
              </Field>
              <Field label="Driver"><TextInput value={f.driver} onChange={set("driver")} /></Field>
              <Field label="Stock Owner" required>
                <SelectInput options={stockOwnerOpts} value={f.stock_owner_id} onChange={set("stock_owner_id")} />
              </Field>
              <Field label="Sender"><SelectInput options={senderOpts} value={f.sender_id} onChange={set("sender_id")} /></Field>
              <Field label="Source Type"><TextInput value={f.source_type} onChange={set("source_type")} /></Field>
              <Field label="Source Ref"><TextInput value={f.source_reference} onChange={set("source_reference")} /></Field>
              <Field label="Stock Item" required>
                <SelectInput options={opt(m.items, (i) => i.name)} value={f.stock_item_id} onChange={set("stock_item_id")} />
              </Field>
              <Field label="Variety"><TextInput value={f.variety} onChange={set("variety")} /></Field>
              <Field label="Grade"><TextInput value={f.grade} onChange={set("grade")} /></Field>
              <Field label="Bag Type">
                <SelectInput options={opt(m.bagTypes, (b) => b.name)} value={f.bag_type_id} onChange={set("bag_type_id")} />
              </Field>
              <Field label="Bags in vehicle" required><NumberInput value={f.bags} onChange={set("bags")} /></Field>
              <Field label="Net Weight (Qtl)" required><NumberInput value={f.net_weight} onChange={set("net_weight")} /></Field>
              <Field label="Torn bags"><NumberInput value={f.torn_qty} onChange={set("torn_qty")} /></Field>
              <Field label="Spill bags"><NumberInput value={f.spill_qty} onChange={set("spill_qty")} /></Field>
              <Field label="Labour Contractor"><SelectInput options={labourOpts} value={f.lab_contractor_id} onChange={set("lab_contractor_id")} /></Field>
              <Field label="Labour rate"><NumberInput value={f.lab_rate} onChange={set("lab_rate")} /></Field>
              <Field label="Expected Dala"><NumberInput value={f.expected_dala} onChange={set("expected_dala")} /></Field>
              <Field label="Dala from vehicle"><NumberInput value={f.dala_from_vehicle} onChange={set("dala_from_vehicle")} /></Field>
              <Field label="Recover dala from"><SelectInput placeholder="" options={CHARGE_WHO} value={f.dala_recover_from} onChange={set("dala_recover_from")} /></Field>
              <Field label="Bag charges to"><SelectInput placeholder="" options={CHARGE_WHO} value={f.bag_charge_to} onChange={set("bag_charge_to")} /></Field>
              <Field label="Remarks" className="col-span-2 md:col-span-3"><TextArea rows={2} value={f.remarks} onChange={set("remarks")} /></Field>
            </div>
            <div className="text-xs text-stone-500">Stock bags after save: {stockBags}</div>
            <button onClick={save} disabled={saving} className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : null} Save changes
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function bagChargeToFromOutward(detail) {
  const row = (detail.bags_detail || []).find((b) => b.charged_to_id);
  if (!row) return "STOCK_OWNER";
  if (row.charge_to_role) return row.charge_to_role;
  if (detail.receiver_id && String(row.charged_to_id) === String(detail.receiver_id)) return "SENDER";
  return "STOCK_OWNER";
}

export function OutwardEditDialog({ record, onClose, onSaved }) {
  const m = useMasters();
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const loadedId = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const { data: detail, isLoading } = useQuery({
    queryKey: ["txn", "outward", record?.id],
    queryFn: async () => (await api.get(`/txn/outward/${record.id}`)).data,
    enabled: !!record?.id,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!record) {
      loadedId.current = null;
      setF(null);
    }
  }, [record]);

  useEffect(() => {
    if (!detail || !record?.id) return;
    if (loadedId.current === record.id) return;
    loadedId.current = record.id;
    const spill = (detail.bags_detail || []).find((b) => b.charge_type === "Spillage");
    const torn = (detail.bags_detail || []).find((b) => b.charge_type === "Torn");
    const spillQty = spill?.quantity || 0;
    const tornQty = torn?.quantity || 0;
    const lab = detail.labour || {};
    const stockBags = detail.stock_bags != null
      ? detail.stock_bags
      : Math.max((detail.bags || 0) - spillQty, 0);
    setF({
      date: detail.date ? String(detail.date).slice(0, 10) : "",
      time: detail.time || "",
      lot_number: detail.lot_number || "",
      warehouse_name: detail.warehouse_name || "",
      stack_code: detail.stack_code || "",
      stock_owner_name: detail.stock_owner_name || "",
      item_name: detail.item_name || "",
      vehicle_id: detail.vehicle_id ? String(detail.vehicle_id) : "",
      vehicle_number: detail.vehicle_number || "",
      driver: detail.driver || "",
      receiver_id: detail.receiver_id ? String(detail.receiver_id) : "",
      destination: detail.destination || "",
      dispatch_reference: detail.dispatch_reference || "",
      bags: stockBags ? String(stockBags) : "",
      net_weight: detail.net_weight != null ? String(detail.net_weight) : "",
      torn_qty: tornQty ? String(tornQty) : "",
      spill_qty: spillQty ? String(spillQty) : "",
      bag_charge_to: bagChargeToFromOutward(detail),
      remarks: detail.remarks || "",
      lab_contractor_id: lab.contractor_id ? String(lab.contractor_id) : "",
      lab_rate: lab.rate != null ? String(lab.rate) : "",
      bag_lab_rate: "",
      expected_dala: lab.expected_dala != null ? String(lab.expected_dala) : "",
      dala_from_vehicle: lab.dala_from_vehicle != null ? String(lab.dala_from_vehicle) : "",
      dala_recover_from: dalaRecoverFrom(lab, detail.stock_owner_id, detail.receiver_id),
      shrinkage_approved: !!detail.shrinkage_approved,
    });
  }, [detail, record?.id]);

  const receiverOpts = opt(partiesByRoles(m.parties, ["Receiver", "Buyer"]), (p) => `${p.name} (${p.code})`);
  const labourOpts = opt(partiesByRole(m.parties, "Labour Contractor"), (p) => `${p.name} (${p.code})`);
  const lotBagTypeId = detail?.bag_type_id;
  const bagType = useMemo(
    () => (m.bagTypes || []).find((b) => String(b.id) === String(lotBagTypeId)),
    [m.bagTypes, lotBagTypeId],
  );
  const bagRate = Number(bagType?.charge_rate || 0);

  const { data: labRate } = useLabourRate(f?.lab_contractor_id, lotBagTypeId);
  useEffect(() => {
    if (!f?.lab_contractor_id || !lotBagTypeId || !labRate?.found) return;
    setF((s) => {
      if (!s) return s;
      const next = { ...s };
      if (s.lab_rate !== String(labRate.loading_rate)) next.lab_rate = String(labRate.loading_rate);
      if (s.bag_lab_rate !== String(labRate.replace_fill_rate ?? "")) next.bag_lab_rate = String(labRate.replace_fill_rate ?? "");
      return next;
    });
  }, [labRate, f?.lab_contractor_id, lotBagTypeId]);

  const canApproveShrink = ["Super Admin", "Management / Owner", "Warehouse Manager"].includes(user?.role);
  const OUTWARD_CHARGE_WHO = [
    { value: "COMPANY", label: "Company bear" },
    { value: "STOCK_OWNER", label: "Stock Owner" },
    { value: "SENDER", label: "Receiver" },
  ];

  const save = async () => {
    if (!f) return;
    if (!f.bags || !f.net_weight) return toast.error("Bags and net weight are required.");
    const tornQty = Number(f.torn_qty || 0);
    const spillQty = Number(f.spill_qty || 0);
    const stockBags = Number(f.bags || 0);
    const labRateNum = Number(f.lab_rate || 0);
    const bagLabRateNum = Number(f.bag_lab_rate || 0);
    const chargeBags = tornQty + spillQty;
    const dalaShort = Math.max(Number(f.expected_dala || 0) - Number(f.dala_from_vehicle || 0), 0);
    if (dalaShort > 0 && f.dala_recover_from === "SENDER" && !f.receiver_id) {
      return toast.error("Select a receiver to recover the dala shortfall from them.");
    }
    if (chargeBags > 0 && !lotBagTypeId) {
      return toast.error("Lot has no bag type — cannot rate torn/spillage charges.");
    }
    if (chargeBags > 0 && f.bag_charge_to === "SENDER" && !f.receiver_id) {
      return toast.error("Select a receiver to charge torn/spillage bags to them.");
    }
    if (chargeBags > 0 && !f.lab_contractor_id) {
      return toast.error("Select a labour contractor — torn/spillage need bag labour (replace/fill) as well as bag material.");
    }
    if (chargeBags > 0 && !bagLabRateNum) {
      return toast.error("No Replace/Fill rate in the contractor agreement for this bag type. Set it under Masters → Labour Rates.");
    }
    setSaving(true);
    try {
      const resolveWho = (who) => {
        if (who === "STOCK_OWNER") return detail?.stock_owner_id ? +detail.stock_owner_id : null;
        if (who === "SENDER") return f.receiver_id ? +f.receiver_id : null;
        return null;
      };
      const contractorId = f.lab_contractor_id ? +f.lab_contractor_id : null;
      const chargedTo = resolveWho(f.bag_charge_to);
      const body = {
        date: f.date,
        time: f.time,
        vehicle_id: matchVehicle(m.vehicles, f.vehicle_id, f.vehicle_number)?.id || null,
        driver: f.driver,
        receiver_id: f.receiver_id ? +f.receiver_id : null,
        destination: f.destination,
        dispatch_reference: f.dispatch_reference,
        bags: stockBags,
        net_weight: +f.net_weight || 0,
        shrinkage_approved: f.shrinkage_approved,
        remarks: f.remarks,
        labour: f.lab_contractor_id
          ? {
              contractor_id: contractorId,
              quantity: stockBags,
              rate: labRateNum,
              work_charged_to_id: detail?.stock_owner_id || null,
              expected_dala: +f.expected_dala || 0,
              dala_from_vehicle: +f.dala_from_vehicle || 0,
              dala_recovery_party_id: resolveWho(f.dala_recover_from),
              dala_recovery_from: f.dala_recover_from,
            }
          : null,
        bags_detail: [
          tornQty && {
            charge_type: "Torn", quantity: tornQty, rate: bagRate,
            labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
            charged_to_id: chargedTo, charge_to_role: f.bag_charge_to,
            reason: "Torn bags replaced on outward",
          },
          spillQty && {
            charge_type: "Spillage", quantity: spillQty, rate: bagRate,
            labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
            charged_to_id: chargedTo, charge_to_role: f.bag_charge_to,
            reason: "Spillage bags filled — added to dispatch, stock not reduced",
          },
        ].filter(Boolean),
      };
      await api.put(`/txn/outward/${record.id}`, body);
      toast.success("Outward updated");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="outward-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Outward {detail?.outward_number || record?.outward_number || record?.doc_number}</DialogTitle>
        </DialogHeader>
        {isLoading || !f ? (
          <div className="grid place-items-center py-12"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
        ) : (
          <>
            <p className="text-xs text-stone-500 -mt-2">
              Lot: {f.lot_number} · {f.warehouse_name}/{f.stack_code} · {f.stock_owner_name} · {f.item_name}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Date"><TextInput type="date" value={f.date} onChange={set("date")} /></Field>
              <Field label="Time"><TextInput type="time" value={f.time} onChange={set("time")} /></Field>
              <Field label="Vehicle">
                <SuggestInput
                  placeholder="Vehicle number"
                  value={f.vehicle_number}
                  options={vehicleOptions(m.vehicles)}
                  onChange={(v) => setF((s) => ({ ...s, vehicle_number: v, vehicle_id: "" }))}
                  onSelect={(o) => setF((s) => ({ ...s, vehicle_id: o.value, vehicle_number: o.label, driver: o.raw?.driver || s.driver }))}
                />
              </Field>
              <Field label="Driver"><TextInput value={f.driver} onChange={set("driver")} /></Field>
              <Field label="Receiver"><SelectInput options={receiverOpts} value={f.receiver_id} onChange={set("receiver_id")} /></Field>
              <Field label="Destination"><TextInput value={f.destination} onChange={set("destination")} /></Field>
              <Field label="Dispatch ref"><TextInput value={f.dispatch_reference} onChange={set("dispatch_reference")} /></Field>
              <Field label="Bags from stock" required><NumberInput value={f.bags} onChange={set("bags")} /></Field>
              <Field label="Net Weight (Qtl)" required><NumberInput value={f.net_weight} onChange={set("net_weight")} /></Field>
              <Field label="Torn bags"><NumberInput value={f.torn_qty} onChange={set("torn_qty")} /></Field>
              <Field label="Spillage bags"><NumberInput value={f.spill_qty} onChange={set("spill_qty")} /></Field>
              <Field label="Bag charges to">
                <SelectInput options={OUTWARD_CHARGE_WHO} value={f.bag_charge_to} onChange={set("bag_charge_to")} placeholder="" />
              </Field>
              <Field label="Labour Contractor"><SelectInput options={labourOpts} value={f.lab_contractor_id} onChange={set("lab_contractor_id")} /></Field>
              <Field label="Labour rate"><NumberInput value={f.lab_rate} onChange={set("lab_rate")} /></Field>
              <Field label="Expected Dala"><NumberInput value={f.expected_dala} onChange={set("expected_dala")} /></Field>
              <Field label="Dala from vehicle"><NumberInput value={f.dala_from_vehicle} onChange={set("dala_from_vehicle")} /></Field>
              <Field label="Recover dala from">
                <SelectInput options={OUTWARD_CHARGE_WHO} value={f.dala_recover_from} onChange={set("dala_recover_from")} placeholder="" />
              </Field>
              {canApproveShrink && (
                <Field label="Approve excess shrinkage" className="col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={f.shrinkage_approved} onChange={(e) => setF((s) => ({ ...s, shrinkage_approved: e.target.checked }))} />
                    Manager approved weight variance over limit
                  </label>
                </Field>
              )}
              <Field label="Remarks" className="col-span-2 md:col-span-3"><TextArea rows={2} value={f.remarks} onChange={set("remarks")} /></Field>
            </div>
            <p className="text-xs text-stone-400">
              Dispatched = stock bags + spillage ({Number(f.bags || 0) + Number(f.spill_qty || 0)}). Spillage does not reduce lot stock.
              Torn/spillage charge bag material + labour to replace/fill.
            </p>
            <button onClick={save} disabled={saving} className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : null} Save changes
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
